import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  ShoppingBag,
  Users,
  Store,
  Bike,
  Scale,
  Wallet,
  Settings as SettingsIcon,
  LogOut,
  Lock,
  ShieldCheck,
  Loader2,
  Menu,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { SITE } from "@/constants/site";
import { validateAdminToken, ADMIN_API_URL } from "@/admin/adminApi";
import { cn } from "@/lib/utils";

const TOKEN_KEY = "halalgo_admin_token";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testId: "admin-nav-overview" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testId: "admin-nav-leads" },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag, testId: "admin-nav-orders" },
  { to: "/admin/users", label: "Users", icon: Users, testId: "admin-nav-users" },
  { to: "/admin/restaurants", label: "Restaurants", icon: Store, testId: "admin-nav-restaurants" },
  { to: "/admin/riders", label: "Riders", icon: Bike, testId: "admin-nav-riders" },
  { to: "/admin/disputes", label: "Disputes", icon: Scale, testId: "admin-nav-disputes" },
  { to: "/admin/settlements", label: "Settlements", icon: Wallet, testId: "admin-nav-settlements" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testId: "admin-nav-settings" },
];

const AdminContext = createContext({ token: "", logout: () => {} });
export const useAdmin = () => useContext(AdminContext);

const SidebarContent = ({ onNavigate }) => (
  <div className="flex h-full flex-col">
    <div className="flex items-center gap-3 px-5 py-5">
      <img src={SITE.logo} alt="Halal GO" className="h-10 w-10 rounded-lg ring-1 ring-white/20" />
      <div>
        <p className="text-sm font-bold leading-none text-white">Halal GO</p>
        <p className="text-[11px] text-white/50">Admin Console</p>
      </div>
    </div>
    <nav className="flex-1 space-y-1 px-3 py-2">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          data-testid={n.testId}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              isActive ? "bg-halalgo-green-600 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
            )
          }
        >
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </nav>
    <div className="border-t border-white/10 px-3 py-3">
      <a
        href="/"
        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-white/60 hover:bg-white/10 hover:text-white"
      >
        <ExternalLink className="h-4 w-4" /> View website
      </a>
    </div>
  </div>
);

const LoginGate = ({ onAuthed }) => {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return toast.error("Please enter the admin token.");
    setLoading(true);
    try {
      await validateAdminToken(input.trim());
      localStorage.setItem(TOKEN_KEY, input.trim());
      onAuthed(input.trim());
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Invalid admin token." : "Could not reach the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="paper-bg flex min-h-screen items-center justify-center px-4" data-testid="admin-page">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl border border-halalgo-kraft-100 bg-white p-8 shadow-lift-md">
        <div className="flex flex-col items-center text-center">
          <img src={SITE.logo} alt="Halal GO" className="h-12 w-12 rounded-lg ring-1 ring-halalgo-kraft-200" />
          <div className="mt-4 flex h-11 w-11 items-center justify-center rounded-xl bg-halalgo-green-50 text-halalgo-green-700">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="mt-3 text-xl font-bold text-halalgo-ink">Admin Console</h1>
          <p className="mt-1 text-sm text-halalgo-ink/60">Enter your admin token to continue.</p>
        </div>
        <div className="mt-6 space-y-2">
          <Label htmlFor="admin-token">Admin token</Label>
          <Input id="admin-token" type="password" data-testid="admin-token-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Paste your admin token" className="h-11" autoComplete="current-password" />
        </div>
        <Button type="submit" disabled={loading} data-testid="admin-unlock-button" className="mt-5 h-11 w-full bg-halalgo-green-600 text-white hover:bg-halalgo-green-700">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Unlock
        </Button>
        <p className="mt-6 text-center text-xs text-halalgo-ink/45">
          <Link to="/" className="hover:text-halalgo-green-700">&larr; Back to website</Link>
        </p>
      </form>
    </div>
  );
};

export default function AdminLayout() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) { setChecking(false); return; }
      try {
        await validateAdminToken(token);
        if (active) setAuthed(true);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        if (active) { setToken(""); setAuthed(false); }
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="paper-bg flex min-h-screen items-center justify-center" data-testid="admin-page">
        <Loader2 className="h-6 w-6 animate-spin text-halalgo-green-700" />
      </div>
    );
  }

  if (!authed) {
    return <LoginGate onAuthed={(t) => { setToken(t); setAuthed(true); }} />;
  }

  const currentLabel = NAV.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)))?.label || "Overview";

  return (
    <AdminContext.Provider value={{ token, logout }}>
      <div className="min-h-screen bg-halalgo-paper" data-testid="admin-page">
        {/* Desktop sidebar */}
        <aside className="fixed inset-y-0 left-0 hidden w-64 bg-halalgo-ink lg:block">
          <SidebarContent />
        </aside>

        <div className="lg:pl-64">
          {/* Topbar */}
          <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-halalgo-kraft-100 bg-white/90 px-4 backdrop-blur sm:px-6">
            <div className="flex items-center gap-3">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-mobile-menu-button" aria-label="Open menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 border-0 bg-halalgo-ink p-0">
                  <SheetHeader><SheetTitle className="sr-only">Admin menu</SheetTitle></SheetHeader>
                  <SidebarContent onNavigate={() => setMobileOpen(false)} />
                </SheetContent>
              </Sheet>
              <h2 className="text-lg font-bold text-halalgo-ink">{currentLabel}</h2>
            </div>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-button" className="border-halalgo-kraft-200">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </header>

          <main className="px-4 py-6 sm:px-6 lg:px-8">
            {!ADMIN_API_URL ? (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                External API URL is not configured. App modules show sample data.
              </div>
            ) : null}
            <Outlet />
          </main>
        </div>
      </div>
    </AdminContext.Provider>
  );
}
